import { Helmet } from 'react-helmet-async';
import { useState, useContext, useCallback, useEffect } from 'react';
// @mui
import {
  Container,
  Stack,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import Iconify from '../components/iconify';
// components
import { TemplateList } from '../sections/@dashboard/templates';

import { EntityContext } from '../entity';

// ----------------------------------------------------------------------

export default function TemplatesPage() {
  const [templateName, setTemplateName] = useState('');
  const [templateType, setTemplateType] = useState('');
  const [templatesList, setTemplatesList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const { getTemplates, createTemplate } = useContext(EntityContext);

  const getDocuments = useCallback(async () => {
    getTemplates().then((data) => {
      if (data && data.templates) {
        setTemplatesList(data.templates);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getDocuments();
  }, [getDocuments]);

  const convertBase64 = (file) =>
    new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => resolve(fileReader.result);
      fileReader.onerror = (error) => reject(error);
    });

  function getExtension(filename) {
    const parts = filename.split('.');
    return parts[parts.length - 1];
  }

  function isDocx(filename) {
    return getExtension(filename).toLowerCase() === 'docx';
  }

  const handleOpenAddDialog = () => {
    setTemplateName('');
    setTemplateType('');
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  const changeHandler = async (event) => {
    const newDoc = event.target.files[0];
    if (!isDocx(newDoc.name)) return;

    const base64Doc = await convertBase64(newDoc);
    const validBase64Doc = base64Doc.split(',');
    const newDocumentRecord = await createTemplate(templateName, validBase64Doc[validBase64Doc.length - 1], templateType);

    if (newDocumentRecord) {
      getDocuments();
      handleCloseAddDialog();
    }
  };

  const filteredTemplates = searchQuery.trim()
    ? templatesList.filter((t) =>
        (t.template_name || '').toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
    : templatesList;

  return (
    <>
      <Helmet>
        <title>Templates</title>
      </Helmet>

      <Container>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2} mb={3}>
          <Typography variant="h4">
            Templates
            {templatesList.length > 0 && (
              <Typography component="span" variant="h4" color="text.secondary" sx={{ ml: 1 }}>
                ({templatesList.length})
              </Typography>
            )}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleOpenAddDialog}
            sx={{ minWidth: { sm: 180 } }}
          >
            Adicionar template
          </Button>
        </Stack>

        {templatesList.length > 0 && (
          <TextField
            placeholder="Buscar por nome..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ mb: 3, width: { xs: '100%', sm: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}

        {filteredTemplates.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 10,
              gap: 2,
              color: 'text.secondary',
            }}
          >
            <FolderOpenOutlinedIcon sx={{ fontSize: 56, opacity: 0.4 }} />
            <Typography variant="body1" color="text.secondary">
              {searchQuery.trim() ? 'Nenhum template encontrado para esta busca.' : 'Nenhum template cadastrado ainda.'}
            </Typography>
            {!searchQuery.trim() && (
              <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleOpenAddDialog}>
                Adicionar template
              </Button>
            )}
          </Box>
        ) : (
          <TemplateList templatesList={filteredTemplates} onDeleted={getDocuments} />
        )}
      </Container>

      {/* Dialog: adicionar template */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="xs" fullWidth aria-labelledby="add-template-dialog-title">
        <DialogTitle id="add-template-dialog-title">Novo template</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Apelido"
              variant="outlined"
              size="small"
              fullWidth
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="template-type-label">Tipo de template</InputLabel>
              <Select
                labelId="template-type-label"
                label="Tipo de template"
                value={templateType}
                onChange={(e) => setTemplateType(e.target.value)}
              >
                <MenuItem value="document">Documento</MenuItem>
                <MenuItem value="endorsement">Termo de Endosso</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              component="label"
              startIcon={<Iconify icon="eva:upload-outline" />}
              disabled={!templateName || !templateType}
              fullWidth
            >
              Selecionar arquivo DOCX
              <input
                hidden
                accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                type="file"
                name="file"
                onChange={changeHandler}
              />
            </Button>
            <Typography variant="caption" color="text.secondary" align="center">
              Apenas arquivos .docx são aceitos.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} color="inherit">
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
